import React from 'react';
import { LogOut, ShieldAlert, X, User, Building2, Truck, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import clsx from 'clsx';

export default function SignOutModal({
  isOpen,
  onClose,
  onConfirm,
  isSigningOut = false
}) {
  const { user, activePortal } = useAuth();

  if (!isOpen) return null;

  const isVendor = activePortal === 'vendor' || user?.type === 'Vendor' || user?.portal_access === 'Vendor Portal';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-950/60 backdrop-blur-xs animate-in fade-in duration-200">
      
      {/* Click-away backdrop */}
      <div 
        className="fixed inset-0" 
        onClick={!isSigningOut ? onClose : undefined} 
      />

      {/* Modal Dialog Content */}
      <div className="relative w-full max-w-md bg-white rounded-3xl border border-slate-200/90 shadow-2xl overflow-hidden z-10 animate-in zoom-in-95 duration-150">
        
        {/* Top Header Glow Bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-rose-500 via-amber-500 to-rose-600" />

        <div className="p-6 sm:p-7 space-y-5">
          
          {/* Header row with Icon & Close */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3.5">
              <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 border border-rose-100 flex items-center justify-center shadow-xs">
                <LogOut className="w-6 h-6 stroke-[2.2]" />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-900 tracking-tight">
                  Sign Out Session
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Terminate current enterprise access
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isSigningOut}
              className="p-1.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* User Account & Workspace Context Badge */}
          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                Active Session
              </span>
              <span className={clsx(
                "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border",
                isVendor 
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
                  : "bg-blue-50 text-blue-700 border-blue-200"
              )}>
                {isVendor ? <Truck className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                <span>{isVendor ? 'Vendor Portal' : 'Client Workspace'}</span>
              </span>
            </div>

            <div className="flex items-center gap-2.5 pt-1">
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-700 text-xs shrink-0">
                {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </div>
              <div className="min-w-0">
                <div className="font-bold text-slate-900 text-xs truncate">
                  {user?.name || user?.email}
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {user?.email}
                </div>
              </div>
            </div>
          </div>

          {/* Confirmation Message */}
          <p className="text-xs text-slate-600 leading-relaxed">
            Are you sure you want to end your active enterprise session? All changes saved to the database will remain safe, but any unsaved form inputs will be cleared.
          </p>

          {/* Modal Action Buttons */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSigningOut}
              className="flex-1 py-2.5 px-4 rounded-xl border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-700 transition-colors cursor-pointer shadow-2xs"
            >
              Stay Signed In
            </button>

            <button
              type="button"
              onClick={onConfirm}
              disabled={isSigningOut}
              className="flex-1 py-2.5 px-4 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold transition-all shadow-xs flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
            >
              {isSigningOut ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Signing out...</span>
                </>
              ) : (
                <>
                  <LogOut className="w-3.5 h-3.5" />
                  <span>Confirm Sign Out</span>
                </>
              )}
            </button>
          </div>

        </div>

      </div>

    </div>
  );
}
